import React, {useContext, useEffect, useState} from 'react';
import './Stoen.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {AdminContext} from '../../contexts/adminContext';
import {Button, Loader, Modal, SelectInput} from '../_common';
import ProjectHeaderItem from './ProjectHeaderItem';
import StoenProjectRow from './StoenProjectRow';
import StoenProjectForm from './StoenProjectForm';
import StoenFilters from './StoenFilters';
import ConfirmationModal from '../_common/ConfirmationModal';
import FullPageLoader from '../_common/FullPageLoader';
import EditSelectedProjectsModal from './EditSelectedProjectsModal';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import FilterListIcon from '@mui/icons-material/FilterList';

const Stoen = ({
  recordsPerPage, 
  changeRecordsPerPage, 
  toggleArchiveBackground, 
  urlProjectId, 
  onUrlProjectClose, 
  urlDateFilters = {}, 
  onUrlDateFiltersClear
}) => {
  const { t } = useContext(I18nContext);
  const {
    pending,
    exportPending,
    stoenProjects,
    stoenPaginationInfo,
    getStoenProjects,
    exportStoenProjects,
    getSingleStoenProject,
    addStoenProject,
    addProjectPending,
    updateStoenProject,
    deleteStoenProject,
    updateStoenProjectState
  } = useContext(ProjectsContext);
  const { usersForDropdowns, getUsersForDropdowns } = useContext(AdminContext);
  const [page, setPage] = useState(0);
  const [jumpToPageInput, setJumpToPageInput] = useState('');
  const [sort, setSort] = useState({sortField: 'dueDate', order: 'ASC'});
  const [filters, setFilters] = useState(urlDateFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showPreviewProjectModal, setShowPreviewProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [dataEdited, setDataEdited] = useState(false);
  const [showDataEditedWarning, setShowDataEditedWarning] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showEditSelectedProjectsModal, setShowEditSelectedProjectsModal] = useState(false);

  const recordsPerPageOptions = [
    {id: 10, label: 10},
    {id: 25, label: 25},
    {id: 50, label: 50},
    {id: 100, label: 100},
  ];

  useEffect(() => {
    getUsersForDropdowns();
  }, []);

  useEffect(() => {
    if (Object.keys(urlDateFilters).length > 0) {
      setFilters(urlDateFilters);
      setPage(0);
    }
  }, [JSON.stringify(urlDateFilters)]);

  useEffect(() => {
    if (!!stoenPaginationInfo && stoenPaginationInfo.number !== page) {
      setPage(stoenPaginationInfo.number);
    }
  }, [stoenPaginationInfo]);

  useEffect(() => {
    if (page || page === 0) {
      getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
    }
  }, [page, sort, filters, showArchivedProjects]);

  useEffect(() => {
    setPage(0);
    getStoenProjects(recordsPerPage, 0, sort, filters, showArchivedProjects);
  }, [recordsPerPage]);

  const changeNumberOfRecordsPerPage = (name, number) => {
    changeRecordsPerPage(number);
  };

  const changePage = () => {
    if (
      jumpToPageInput !== stoenPaginationInfo.number + 1 &&
      jumpToPageInput <= stoenPaginationInfo.totalPages &&
      jumpToPageInput > 0)
    {
      setPage(jumpToPageInput - 1);
      setJumpToPageInput('');
    }
  };

  const handleChangePageOnEnterPress = e => {
    if (e.keyCode === 13) {
      changePage();
    }
  };

  const changePageUp = () => {
    if (page < stoenPaginationInfo.totalPages - 1) {
      setPage(page + 1);
    }
  };

  const changePageDown = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const changePageFirst = () => {
    setPage(0);
  };

  const changePageLast = () => {
    setPage(stoenPaginationInfo.totalPages - 1);
  };

  const applySort = (sortField, order) => {
    setSort({sortField, order});
  };

  const toggleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  const applyFilters = filters => {
    setFilters(filters);
    setPage(0);
    if (!filters.fromLastModificationDate && !filters.toLastModificationDate) {
      onUrlDateFiltersClear?.();
    }
  };

  const toggleShowAddProjectModal = () => {
    if (showAddProjectModal && dataEdited) {
      setShowDataEditedWarning(true);
    } else {
      setShowAddProjectModal(!showAddProjectModal);
    }
  };

  const addNewProject = async payload => {
    setDataEdited(false);
    const newProject = await addStoenProject(payload);
    setShowAddProjectModal(false);
    getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  const openEditProjectModal = async id => {
    const project = await getSingleStoenProject(id);
    if (project) {
      setSelectedProject(project);
      setShowEditProjectModal(true);
    }
  };

  const closeEditProjectModal = noCheckForEditedData => {
    if (dataEdited && !noCheckForEditedData) {
      setShowDataEditedWarning(true);
    } else {
      setShowEditProjectModal(false);
      setSelectedProject(null);
      onUrlProjectClose?.();
    }
    getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  const editProject = async (payload, projectId, version) => {
    setDataEdited(false);
    const updatedProject = await updateStoenProject(payload, projectId, version);
    setShowEditProjectModal(false);
    setSelectedProject(null);
    getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
    onUrlProjectClose?.();
  };

  const openDeleteProjectModal = async id => {
    setSelectedProject(await getSingleStoenProject(id));
    setShowDeleteProjectModal(true);
  };

  const closeDeleteProjectModal = () => {
    setShowDeleteProjectModal(false);
    setSelectedProject(null);
  };

  const deleteProject = async id => {
    const deletedProject = await deleteStoenProject(id);
    setShowDeleteProjectModal(false);
    setSelectedProject(null);
    getStoenProjects(recordsPerPage, 0, sort, filters, showArchivedProjects);
  };

  const refreshProjectsList = () => {
    getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  const openPreviewProjectModal = async id => {
    const project = await getSingleStoenProject(id);
    if (project) {
      setSelectedProject(project);
      setShowPreviewProjectModal(true);
    }
  };

  const closePreviewProjectModal = () => {
    setShowPreviewProjectModal(false);
    setSelectedProject(null);
    getStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  useEffect(() => {
    if (urlProjectId) {
      openEditProjectModal(urlProjectId);
    }
  }, [urlProjectId]);

  const finishProject = async projectId => {
    await updateStoenProjectState('completed', projectId, t('finished'));
    refreshProjectsList();
  };

  const cancelProject = async projectId => {
    await updateStoenProjectState('canceled', projectId, t('canceled'));
    refreshProjectsList();
  };

  const reactivateProject = async projectId => {
    await updateStoenProjectState('project', projectId, t('reactivated'));
    refreshProjectsList();
  };

  const toggleShowArchivedProjects = () => {
    setShowArchivedProjects(!showArchivedProjects);
    toggleArchiveBackground();
  };

  const confirmCloseProjectWithEditedData = () => {
    setDataEdited(false);
    if (showAddProjectModal) {
      setShowAddProjectModal(false);
    }
    if (showEditProjectModal) {
      setShowEditProjectModal(false);
      setSelectedProject(null);
      onUrlProjectClose?.();
    }
    setShowDataEditedWarning(false);
  };

  const refreshSelectedProject = project => {
    setSelectedProject(project);
  };

  const exportProjectsToExcel = () => {
    exportStoenProjects(recordsPerPage, page, sort, filters, showArchivedProjects)
  };

  const toggleSelectProject = id => {
    const newSelectedProjects = [...selectedProjects];
    const index = selectedProjects.indexOf(id);

    if (newSelectedProjects.includes(id)) {
      newSelectedProjects.splice(index, 1)
    } else {
      newSelectedProjects.push(id);
    }

    setSelectedProjects(newSelectedProjects);
  };

  const openEditMultipleProjectsModal = async () => {
    setShowEditSelectedProjectsModal(true);
  };

  const closeEditMultipleProjectsModal = () => {
    setShowEditSelectedProjectsModal(false);
  };

  const clearProjectsSelection = () => {
    setSelectedProjects([]);
  };

  const checkIfAllProjectsSelected = () => {
    let allSelected = true;
    stoenProjects.map(project => project.id).forEach(id => {
      if (!selectedProjects.includes(id)) {
        allSelected = false;
      }
    })
    return allSelected;
  };

  const toggleSelectAllProjects = () => {
    let newSelectedProjects = [...selectedProjects];

    if (checkIfAllProjectsSelected()) {
      stoenProjects.map(project => project.id).forEach(id => {
        if (newSelectedProjects.includes(id)) {
          const index = newSelectedProjects.indexOf(id);
          newSelectedProjects.splice(index, 1);
        }
      })
    } else {
      stoenProjects.map(project => project.id).forEach(id => {
        if (!newSelectedProjects.includes(id)) {
          newSelectedProjects.push(id);
        }
      })
    }

    setSelectedProjects(newSelectedProjects)
  };

  const toggleMarkProjectAsUrgent = async (id, value) => {
    const projectObj = await getSingleStoenProject(id);
    await updateStoenProject({urgent: value}, id, projectObj?.version, true);
    refreshProjectsList();
  };

  const toggleMarkProjectAsToBePlanned = async (id, value) => {
    const projectObj = await getSingleStoenProject(id);
    await updateStoenProject({toBePlanned: value}, id, projectObj?.version, true);
    refreshProjectsList();
  };

  return (
    <div className='Stoen'>
      <div className='header'>
        <div className='filters'>
          <StoenFilters
            filters={filters}
            showFilters={showFilters}
            toggleShowFilters={toggleShowFilters}
            setFilters={applyFilters}
          />
        </div>
        <div className='filtersMobile'>
          <div className='filtersButton' onClick={toggleShowFilters}>
            <FilterListIcon />
            <div className='filtersButtonText'>
              {t('filters')}
            </div>
          </div>
          {showFilters && (
            <StoenFilters
              filters={filters}
              showFilters={showFilters}
              toggleShowFilters={toggleShowFilters}
              setFilters={applyFilters}
            />
          )}
        </div>
        <div className='headerButtons'>
          <div className='buttonsGroup'>
            <Button
              label={t('export')}
              action={exportProjectsToExcel}
              pending={exportPending}
            />
            <Button
              type='secondary'
              label={`${t('edit selected')} (${selectedProjects.length})`}
              action={openEditMultipleProjectsModal}
              disabled={!selectedProjects.length}
            />
            {!!selectedProjects.length && (
              <Button
                type='secondary'
                label={t('clear selection')}
                action={clearProjectsSelection}
                disabled={!selectedProjects.length}
              />
            )}
          </div>
          <div className='buttonsGroup'>
            <Button
              label={!showArchivedProjects ? t('go to archived projects') : t('go to ongoing projects')}
              action={toggleShowArchivedProjects}
            />
            <Button type='secondary' label={t('add project')} action={toggleShowAddProjectModal}/>
          </div>
        </div>
      </div>
      <div className='headerTitle'>
        {!showArchivedProjects ? t('active projects') : t('archived projects')}
        {' '}
        {`(${t('found', 'lower')} ${stoenPaginationInfo.totalRecords})`}
      </div>
      <div className='projectsTable'>
        {!pending && !!stoenPaginationInfo && !!stoenPaginationInfo?.totalPages && (
          <div className='paginationRow'>
            <div className='paginationRowItem'>
              <SelectInput
                label={t('records per page')}
                options={recordsPerPageOptions}
                onSelect={changeNumberOfRecordsPerPage}
                value={recordsPerPage}
                small
                inline
                noMargin
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${stoenPaginationInfo.totalPages === 1 || !jumpToPageInput.length
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePage}
              >
                {t('go to page')}
              </div>
              <input
                type='number'
                className={`paginationInput ${stoenPaginationInfo.totalPages === 1 ? 'paginationInputDisabled' : ''}`}
                onChange={e => setJumpToPageInput(e.target.value)}
                onKeyDown={handleChangePageOnEnterPress}
                disabled={stoenPaginationInfo.totalPages === 1}
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginRight
                  ${stoenPaginationInfo.number === 0 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageFirst}
              >
                <FirstPageIcon />
              </div>
              <div
                className={`paginationButton ${stoenPaginationInfo.number === 0 ? 'paginationDisabled' : ''}`}
                onClick={changePageDown}
              >
                <NavigateBeforeIcon />
              </div>
              <div className='paginationInfo'>
                {t('page')} {stoenPaginationInfo.number + 1}/{stoenPaginationInfo.totalPages}
              </div>
              <div
                className={`paginationButton ${stoenPaginationInfo.number === stoenPaginationInfo.totalPages - 1
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePageUp}
              >
                <NavigateNextIcon />
              </div>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginLeft 
                  ${stoenPaginationInfo.number === stoenPaginationInfo.totalPages - 1 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageLast}
              >
                <LastPageIcon />
              </div>
            </div>
          </div>
        )}

        <div className='projectsTableHeader'>
          <div className='headerItemSelect'>
            <input
              className='selectAllProjectsCheckbox'
              type='checkbox'
              checked={checkIfAllProjectsSelected()}
              onChange={toggleSelectAllProjects}
            />
          </div>
          <div className='headerItemMeatball'/>
          <div className='headerItemUrgentMark'/>
          {/*<div className='headerItemToBePlannedMark'/>*/}
          <div className='ordinalNumber'>#</div>
          <ProjectHeaderItem
            label={t('investment address')}
            fieldName='investmentAddress'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('order number')}
            fieldName='orderNumber'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('conditions number')}
            fieldName='conditionsNumber'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('designer')}
            noSort
          />
          <ProjectHeaderItem
            label={t('client name')}
            fieldName='clientFullName'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('due date')}
            fieldName='dueDate'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('created at')}
            fieldName='createdAt'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('last modified')}
            fieldName='lastModificationDate'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('order cost')}
            fieldName='cost'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('project state')}
            fieldName='projectState'
            applySort={applySort}
            sort={sort}
          />
        </div>

        {!!pending && (
          <div className='loaderWrapper'><Loader/></div>
        )}

        {!pending && !stoenProjects.length && (
          <div className='noProjectsInfo'>
            {t('no projects meeting the criteria')}
          </div>
        )}

        {!pending && !!usersForDropdowns.length && stoenProjects.map((project, i) => (
          <StoenProjectRow
            key={project.id}
            project={project}
            refreshProjectsList={() => getStoenProjects(10, page)}
            users={usersForDropdowns}
            openEditProjectModal={() => openEditProjectModal(project.id)}
            openDeleteProjectModal={() => openDeleteProjectModal(project.id)}
            openPreviewProjectModal={() => openPreviewProjectModal(project.id)}
            finishProject={finishProject}
            cancelProject={cancelProject}
            reactivateProject={reactivateProject}
            ordinalNumber={i + 1}
            toggleSelectProject={toggleSelectProject}
            isSelected={selectedProjects.includes(project.id)}
            toggleMarkProjectAsUrgent={toggleMarkProjectAsUrgent}
            toggleMarkProjectAsToBePlanned={toggleMarkProjectAsToBePlanned}
          />
        ))}

        {!pending && !!stoenPaginationInfo && !!stoenPaginationInfo?.totalPages && (
          <div className='paginationRow'>
            <div className='paginationRowItem'>
              <SelectInput
                label={t('records per page')}
                options={recordsPerPageOptions}
                onSelect={changeNumberOfRecordsPerPage}
                value={recordsPerPage}
                small
                inline
                noMargin
                dropdownOnTop
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${stoenPaginationInfo.totalPages === 1 || !jumpToPageInput.length
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePage}
              >
                {t('go to page')}
              </div>
              <input
                type='number'
                className={`paginationInput ${stoenPaginationInfo.totalPages === 1 ? 'paginationInputDisabled' : ''}`}
                onChange={e => setJumpToPageInput(e.target.value)}
                onKeyDown={handleChangePageOnEnterPress}
                disabled={stoenPaginationInfo.totalPages === 1}
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginRight
                  ${stoenPaginationInfo.number === 0 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageFirst}
              >
                <FirstPageIcon />
              </div>
              <div
                className={`paginationButton ${stoenPaginationInfo.number === 0 ? 'paginationDisabled' : ''}`}
                onClick={changePageDown}
              >
                <NavigateBeforeIcon />
              </div>
              <div className='paginationInfo'>
                {t('page')} {stoenPaginationInfo.number + 1}/{stoenPaginationInfo.totalPages}
              </div>
              <div
                className={`paginationButton ${stoenPaginationInfo.number === stoenPaginationInfo.totalPages - 1
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePageUp}
              >
                <NavigateNextIcon />
              </div>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginLeft 
                  ${stoenPaginationInfo.number === stoenPaginationInfo.totalPages - 1 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageLast}
              >
                <LastPageIcon />
              </div>
            </div>
          </div>
        )}
      </div>

      {!!showAddProjectModal && (
        <Modal
          closeModal={toggleShowAddProjectModal}
          title={t('add project')}
          width='90%'
          height='630px'
        >
          <StoenProjectForm
            users={usersForDropdowns}
            primaryButtonAction={addNewProject}
            secondaryButtonAction={toggleShowAddProjectModal}
            setDataAsEdited={() => setDataEdited(true)}
          />
        </Modal>
      )}

      {!!showEditProjectModal && (
        <Modal
          closeModal={closeEditProjectModal}
          title={t('edit project')}
          width='90%'
          height='90%'
        >
          <StoenProjectForm
            project={selectedProject}
            users={usersForDropdowns}
            primaryButtonAction={editProject}
            secondaryButtonAction={closeEditProjectModal}
            refreshProjectsList={refreshProjectsList}
            setDataAsEdited={() => setDataEdited(true)}
            refreshSelectedProject={refreshSelectedProject}
          />
        </Modal>
      )}

      {!!showPreviewProjectModal && (
        <Modal
          closeModal={closePreviewProjectModal}
          title={t('preview project')}
          width='90%'
          height='90%'
        >
          <StoenProjectForm
            project={selectedProject}
            users={usersForDropdowns}
            secondaryButtonAction={closePreviewProjectModal}
            refreshProjectsList={refreshProjectsList}
            preview
          />
        </Modal>
      )}

      {!!showDataEditedWarning && (
        <ConfirmationModal
          modalText={t('are you sure you want to close the project form and loose unsaved changes')}
          closeModal={() => setShowDataEditedWarning(false)}
          title={t('data has been edited')}
          itemName=''
          primaryButtonAction={confirmCloseProjectWithEditedData}
          primaryButtonLabel={t('close project')}
        />
      )}

      {!!showDeleteProjectModal && (
        <ConfirmationModal
          modalText={t('are you sure you want to delete the project')}
          closeModal={closeDeleteProjectModal}
          title={t('delete project')}
          itemName={selectedProject.investmentAddress}
          primaryButtonAction={() => deleteProject(selectedProject.id)}
          primaryButtonLabel={t('delete project')}
        />
      )}

      {!!showEditSelectedProjectsModal && (
        <EditSelectedProjectsModal
          selectedProjects={selectedProjects}
          closeModal={closeEditMultipleProjectsModal}
          refreshProjectsList={refreshProjectsList}
          clearProjectsSelection={clearProjectsSelection}
          stoen
        />
      )}

      {!!addProjectPending && (
        <FullPageLoader />
      )}
    </div>
  );
};

export default Stoen;
